/**
 * gammaAttribution.ts — MER Algo Footprints v7.1B — Phase 6.5.1
 *
 * Canonical Gamma performance attribution record, builder, and merged bundle.
 *
 * Phase 6.5: One GammaAttributionRecord per setup (confidence) or per symbol (ranking).
 * Phase 6.5.1 additions:
 *   - correlationKey: deterministic shared key for conf+ranking records per decision
 *   - buildCorrelationKey: exported pure key generator
 *   - GammaMergedAttributionBundle: analysis view joining both sides
 *   - buildMergedBundle: pure helper building a bundle from correlated records
 *   - evaluationCycleId on GammaAttributionRecord and AttributionBuildInput: explicit
 *     cycle identity for joining confidence + ranking records
 *   - buildCorrelationKey now uses evaluationCycleId, not cycleMs
 *
 * Placement: src/domains/market/engines/gamma/gammaAttribution.ts
 */

import type { GammaInfluenceAudit } from "./gammaInfluenceTypes";
import type { GammaRankingAudit }   from "./gammaRankingInfluence";

// ---Correlation key---

/**
 * Builds the canonical correlationKey from an evaluationCycleId.
 * The ID is an explicit per-symbol-cycle identity (e.g. "NVDA_cycle_3"),
 * not a raw timestamp. This makes joining deterministic and debuggable.
 */
export function buildCorrelationKey(symbol: string, setupType: string, evaluationCycleId: string): string {
  const norm = setupType.replace(/\s+/g, "_").replace(/[^A-Za-z0-9_]/g, "");
  return symbol.toUpperCase() + "_" + norm + "_" + evaluationCycleId;
}

// ---Attribution record---

export interface GammaAttributionRecord {
  id:             string;
  /** Explicit per-symbol-cycle identity, e.g. "NVDA_cycle_3".
   *  The canonical join key between confidence and ranking attribution records.
   *  Created once per scoring cycle by startEvaluationCycle() in dashboardView. */
  evaluationCycleId: string;
  /** Composite correlation key derived from evaluationCycleId + setupType.
   *  Use evaluationCycleId for primary joining; correlationKey for setup-level grouping. */
  correlationKey: string;
  capturedAt:     number;
  symbol:         string;
  setupType:      string;
  setupDir:       string;
  gammaAvailable:   boolean;
  gammaStale:       boolean | null;
  dataQuality:      string | null;
  regime:           string | null;
  bias:             string | null;
  gammaScore:       number | null;
  pinScore:         number | null;
  expansionScore:   number | null;
  directionalScore: number | null;
  baseConfidence:       number;
  confidenceAdjustment: number;
  finalConfidence:      number;
  confidenceRule:       string;
  confidenceSkipReason: string | null;
  baseRankScore:     number | null;
  rankingAdjustment: number;
  finalRankScore:    number | null;
  rankingRule:       string;
  rankingSkipReason: string | null;
  outcomeHorizon:     string | null;
  outcomeReturn:      number | null;
  outcomeMFE:         number | null;
  outcomeMAE:         number | null;
  outcomeSuccess:     boolean | null;
  outcomeEvaluatedAt: number | null;

  // ── Promotion metadata (Phase 6.8, optional) ──────────────────────────────
  /** ID of the promotion profile that was considered for this evaluation. */
  promotionProfileId?: string;
  /** Rollout decision: which flags actually applied. */
  rolloutDecision?:    string;
  /** True when this record was captured in shadow mode (candidate evaluated but not applied live). */
  isShadowOnly?:       boolean;
}

// ---Builder input---

export interface AttributionBuildInput {
  capturedAt:      number;
  symbol:          string;
  setupType:       string;
  setupDir:        string;
  baseConfidence:  number;
  baseRankScore:   number | null;
  confidenceAudit: GammaInfluenceAudit | null;
  rankingAudit:    GammaRankingAudit   | null;
  /** Explicit evaluation cycle identity for this scoring pass.
   *  Created once per symbol scoring cycle by startEvaluationCycle() in dashboardView
   *  and read by buildSymbolContext via getEvaluationCycleId().
   *  When the same evaluationCycleId is used by confidence and ranking captures,
   *  both records share a correlationKey and evaluationCycleId for joining.
   *  Falls back to a timestamp-derived ID when not provided (direct/test use). */
  evaluationCycleId?: string;
  /** Phase 6.8: Promotion profile ID that was consulted for this evaluation. */
  promotionProfileId?: string;
  /** Phase 6.8: Rollout decision that was applied. */
  rolloutDecision?:    string;
  /** Phase 6.8: True when captured as a shadow evaluation (candidate not applied live). */
  isShadowOnly?:       boolean;
}

function makeId(symbol: string, setupType: string, ts: number): string {
  return symbol + "_" + setupType.replace(/\s+/g, "_") + "_" + ts;
}

export function buildAttributionRecord(input: AttributionBuildInput): GammaAttributionRecord {
  const { capturedAt, symbol, setupType, setupDir, baseConfidence, baseRankScore,
          confidenceAudit: ca, rankingAudit: ra, evaluationCycleId } = input;

  // Derive a stable cycle ID: prefer explicit ID, fall back to timestamp-string
  const cycleId        = evaluationCycleId ?? (symbol.toUpperCase() + "_cycle_ts_" + capturedAt);
  const correlationKey = buildCorrelationKey(symbol, setupType, cycleId);

  const gammaAvailable   = ca?.gammaAvailable   ?? ra?.gammaAvailable   ?? false;
  const gammaStale       = ca?.gammaStale        ?? ra?.gammaStale        ?? null;
  const dataQuality      = ca?.dataQuality       ?? ra?.dataQuality       ?? null;
  const regime           = ca?.regime            ?? ra?.regime            ?? null;
  const bias             = ca?.bias              ?? ra?.bias              ?? null;
  const pinScore         = ca?.pinScore          ?? ra?.pinScore          ?? null;
  const expansionScore   = ca?.expansionScore    ?? ra?.expansionScore    ?? null;
  const directionalScore = ca?.directionalScore  ?? ra?.directionalScore  ?? null;
  const gammaScore: number | null = (pinScore !== null && expansionScore !== null)
    ? Math.round((pinScore + expansionScore) / 2) : null;

  const confidenceAdjustment = ca?.adjustment       ?? 0;
  const finalConfidence      = ca?.resultConfidence ?? baseConfidence;
  const confidenceRule       = ca?.rule             ?? "NOT_RUN";
  const confidenceSkipReason = (ca?.rule === "SKIP") ? ca.reason : null;

  const rankingAdjustment = ra?.rankingAdjustment ?? 0;
  const finalRankScore    = ra?.finalScore        ?? baseRankScore;
  const rankingRule       = ra?.rule              ?? "NOT_RUN";
  const rankingSkipReason = (ra?.rule === "SKIP") ? ra.reason : null;

  return {
    id: makeId(symbol, setupType, capturedAt),
    evaluationCycleId: cycleId,
    correlationKey, capturedAt, symbol, setupType, setupDir,
    promotionProfileId: input.promotionProfileId,
    rolloutDecision:    input.rolloutDecision,
    isShadowOnly:       input.isShadowOnly ?? false,
    gammaAvailable, gammaStale, dataQuality, regime, bias,
    gammaScore, pinScore, expansionScore, directionalScore,
    baseConfidence, confidenceAdjustment, finalConfidence,
    confidenceRule, confidenceSkipReason,
    baseRankScore, rankingAdjustment, finalRankScore,
    rankingRule, rankingSkipReason,
    outcomeHorizon: null, outcomeReturn: null, outcomeMFE: null,
    outcomeMAE: null, outcomeSuccess: null, outcomeEvaluatedAt: null,
  };
}

// ---Merged attribution bundle---

export interface GammaMergedAttributionBundle {
  /** The explicit evaluation cycle identity shared by both constituent records. */
  evaluationCycleId: string;
  correlationKey: string;
  symbol: string; setupType: string; setupDir: string;
  gammaAvailable: boolean; gammaStale: boolean | null; dataQuality: string | null;
  regime: string | null; bias: string | null;
  gammaScore: number | null; pinScore: number | null;
  expansionScore: number | null; directionalScore: number | null;
  baseConfidence: number; confidenceAdjustment: number; finalConfidence: number;
  confidenceRule: string; confidenceSkipReason: string | null;
  confidenceCapturedAt: number | null;
  baseRankScore: number | null; rankingAdjustment: number; finalRankScore: number | null;
  rankingRule: string; rankingSkipReason: string | null; rankingCapturedAt: number | null;
  totalGammaEffect: number;
  anyInfluenceApplied: boolean;
  outcomeHorizon: string | null; outcomeReturn: number | null;
  outcomeMFE: number | null; outcomeMAE: number | null; outcomeSuccess: boolean | null;
}

export function buildMergedBundle(
  confidenceRecord: GammaAttributionRecord | null,
  rankingRecord:    GammaAttributionRecord | null,
): GammaMergedAttributionBundle | null {
  if (!confidenceRecord && !rankingRecord) return null;
  const p  = confidenceRecord ?? rankingRecord!;
  const rr = rankingRecord ?? p;
  const os = [confidenceRecord, rankingRecord].find(r => r?.outcomeReturn !== null);
  const totalGammaEffect = p.confidenceAdjustment + rr.rankingAdjustment;
  return {
    evaluationCycleId: p.evaluationCycleId,
    correlationKey: p.correlationKey,
    symbol: p.symbol, setupType: p.setupType, setupDir: p.setupDir,
    gammaAvailable: p.gammaAvailable, gammaStale: p.gammaStale,
    dataQuality: p.dataQuality, regime: p.regime, bias: p.bias,
    gammaScore: p.gammaScore, pinScore: p.pinScore,
    expansionScore: p.expansionScore, directionalScore: p.directionalScore,
    baseConfidence: p.baseConfidence, confidenceAdjustment: p.confidenceAdjustment,
    finalConfidence: p.finalConfidence, confidenceRule: p.confidenceRule,
    confidenceSkipReason: p.confidenceSkipReason,
    confidenceCapturedAt: confidenceRecord?.capturedAt ?? null,
    baseRankScore: rr.baseRankScore, rankingAdjustment: rr.rankingAdjustment,
    finalRankScore: rr.finalRankScore, rankingRule: rr.rankingRule,
    rankingSkipReason: rr.rankingSkipReason,
    rankingCapturedAt: rankingRecord?.capturedAt ?? null,
    totalGammaEffect,
    anyInfluenceApplied: totalGammaEffect !== 0,
    outcomeHorizon: os?.outcomeHorizon ?? null, outcomeReturn: os?.outcomeReturn ?? null,
    outcomeMFE: os?.outcomeMFE ?? null, outcomeMAE: os?.outcomeMAE ?? null,
    outcomeSuccess: os?.outcomeSuccess ?? null,
  };
}
